import { useEffect, useMemo, useState } from 'react';
import { isDateRangeAvailable } from '@/utils/dateAvailability';
import { Property } from '@/types/property';

export type FilterOptions = {
  optimized?: boolean;
  guestsCompare?: 'maxCapacity' | 'lte';
};

type Inputs = {
  availableProperties: Property[];
  propertyIds: string[];
  availability: Record<
    string,
    { unavailableDates?: Date[]; isLoading?: boolean }
  >;
  checkInDate: Date | null;
  checkOutDate: Date | null;
  guestCount: number | null;
  snapshot: string;
} & FilterOptions;

export const useFilteredProperties = ({
  availableProperties,
  propertyIds,
  availability,
  checkInDate,
  checkOutDate,
  guestCount,
  snapshot,
  optimized = true,
  guestsCompare = 'maxCapacity',
}: Inputs) => {
  const [filtered, setFiltered] = useState<Property[]>([]);

  const allAvailabilityReady = useMemo(() => {
    if (optimized) return true;
    return (
      propertyIds.length > 0 &&
      propertyIds.every((id) => {
        const av = availability[id];
        return av && !av.isLoading && Array.isArray(av.unavailableDates);
      })
    );
  }, [optimized, propertyIds, snapshot, availability]);

  useEffect(() => {
    if (!optimized && !allAvailabilityReady) return;

    let next = availableProperties;

    if (guestCount !== null) {
      next = next.filter((p) => {
        if (typeof p.guests !== 'number') return true;
        return guestsCompare === 'maxCapacity'
          ? p.guests >= guestCount
          : p.guests <= guestCount;
      });
    }

    if (checkInDate && checkOutDate) {
      next = next.filter((p) => {
        const av = availability[p.id];
        if (!av) return false;

        if (!Array.isArray(av.unavailableDates)) {
          return optimized ? true : false;
        }

        return isDateRangeAvailable(
          checkInDate,
          checkOutDate,
          av.unavailableDates,
        );
      });
    }

    setFiltered((prev) => {
      if (
        prev.length === next.length &&
        prev.every((a, i) => a.id === next[i].id)
      )
        return prev;
      return next;
    });
  }, [
    optimized,
    allAvailabilityReady,
    availableProperties,
    guestCount,
    guestsCompare,
    checkInDate,
    checkOutDate,
    snapshot,
    propertyIds,
    availability,
  ]);

  return { filtered, allAvailabilityReady };
};
